/* eslint-disable linebreak-style */
import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DocDetailsDialog from "./docdetails";
import axios from "axios";
import {
  hatcheryDataUrl,
  allFarmsUrl,
  updateHatcheryDataUrl,
  docDataUrl,
  updateDOCTableUrl,
  removeDOCTableUrl,
} from "api/endpoints";
import {
  readOfflineRecords,
  upsertOfflineRecord,
  removeOfflineRecord,
  replaceOfflineRecords,
  clearOfflineRecords,
} from "utils/offlineRecords";

function SectionTitle({ children }) {
  return (
    <Box mt={2} mb={1}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          bgcolor: "grey.200",
          px: 1,
          py: 0.5,
          fontSize: "0.95rem",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

function Inputs(
  { facilityId, cycleId, farmId, selectedHatcheryDataId, selectedInvoiceNumber },
  ref
) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const prevSelectionRef = React.useRef({
    facilityId: null,
    cycleId: null,
    selectedHatcheryDataId: null,
  });
  const [hatchery, setHatchery] = useState("");
  const [hatcheryOptions, setHatcheryOptions] = useState([]); // list of hatchery IDs as strings
  const [hatcheryMap, setHatcheryMap] = useState({}); // id -> description
  const [date, setDate] = useState("");
  const [existingId, setExistingId] = useState(null);

  // Delivery details
  const [waybill, setWaybill] = useState("");
  const [timeOfDelivery, setTimeOfDelivery] = useState("");
  const deliveryPickerRef = React.useRef(null);
  const startPickerRef = React.useRef(null);
  const [totalBoxes, setTotalBoxes] = useState("");
  const [birdsPerBox, setBirdsPerBox] = useState("");

  // DOC details
  const [docRows, setDocRows] = useState([]);
  // Removed pagination; show all DOC rows
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docEditOpen, setDocEditOpen] = useState(false);
  const [docEditInitial, setDocEditInitial] = useState(null);
  const [docEditId, setDocEditId] = useState(null);
  const [offlineTick, setOfflineTick] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return () => {};
    const handleOfflineSyncComplete = () => {
      setOfflineTick((tick) => tick + 1);
    };
    window.addEventListener("omnio:offline-sync-complete", handleOfflineSyncComplete);
    return () => {
      window.removeEventListener("omnio:offline-sync-complete", handleOfflineSyncComplete);
    };
  }, []);

  const selectionKeys = useMemo(
    () => ({
      facilityId,
      cycleId,
      farmId,
      offlineTick,
      selectedHatcheryDataId,
      selectedInvoiceNumber,
    }),
    [facilityId, cycleId, farmId, offlineTick, selectedHatcheryDataId, selectedInvoiceNumber]
  );

  const OFFLINE_QUEUE_STORAGE_KEY = "OmnioOfflineQueue";
  const OFFLINE_NS_INPUTS = "SetupCycleInputs";
  const OFFLINE_NS_DOC = "SetupCycleDoc";

  const readOfflineQueue = () => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(OFFLINE_QUEUE_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn("Unable to read offline queue", error);
      return [];
    }
  };

  const writeOfflineQueue = (queue) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(OFFLINE_QUEUE_STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.warn("Unable to update offline queue", error);
    }
  };

  const removeQueueEntry = (queueId) => {
    if (!queueId) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;
    const filtered = queue.filter((entry) => entry && `${entry.id}` !== `${queueId}`);
    if (filtered.length !== queue.length) {
      writeOfflineQueue(filtered);
    }
  };

  const readCachedCollection = (url) => {
    if (typeof window === "undefined") return null;
    try {
      const key = `OmnioCache:get:${url}?`;
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const data = parsed?.data;
      if (Array.isArray(data)) return data;
      if (Array.isArray(parsed)) return parsed;
      return null;
    } catch (error) {
      console.warn("Unable to read cached collection for", url, error);
      return null;
    }
  };

  const parseQueuePayload = (entry) => {
    if (!entry?.data) return null;
    if (entry.data.type === "json") {
      try {
        return entry.data.body ? JSON.parse(entry.data.body) : null;
      } catch (error) {
        console.warn("Unable to parse queued payload", error);
        return null;
      }
    }
    if (entry.data.type === "string") {
      const raw = entry.data.body;
      if (raw == null) return null;
      try {
        return JSON.parse(raw);
      } catch (error) {
        return { __raw: raw };
      }
    }
    return null;
  };

  const wasQueuedOffline = (response, error) => {
    if (navigator?.onLine === false) return true;
    if (response?.status === 202) return true;
    if (response?.data?.offline || response?.data?.queued) return true;
    if (error) {
      const status = error?.response?.status;
      if (!status && (error?.code === "ERR_NETWORK" || error?.message?.includes("Network"))) {
        return true;
      }
    }
    return false;
  };

  const mergeOfflineRecords = (cachedRows, offlineRecords) => {
    const offlineArray = Array.isArray(offlineRecords) ? offlineRecords : [];
    const offlineRows = offlineArray
      .filter((entry) => !entry?.__deleted)
      .map((entry) => ({ ...entry, __isOffline: true }));
    const deletedIds = new Set(
      offlineArray.filter((entry) => entry?.__deleted).map((entry) => `${entry.id}`)
    );
    const offlineIds = new Set(offlineRows.map((entry) => `${entry.id}`));
    const filteredCached = (Array.isArray(cachedRows) ? cachedRows : []).filter(
      (entry) => !offlineIds.has(`${entry.id}`) && !deletedIds.has(`${entry.id}`)
    );
    return [...offlineRows, ...filteredCached];
  };

  const normalizeForComparison = (value) => {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "number") {
      if (!Number.isFinite(value)) return null;
      return Number(value.toFixed(6));
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (/^[+-]?\d+(\.\d+)?$/.test(trimmed)) {
        const num = Number(trimmed);
        if (!Number.isNaN(num)) return Number(num.toFixed(6));
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || trimmed.includes("T")) {
        const parsed = Date.parse(trimmed);
        if (!Number.isNaN(parsed)) return new Date(parsed).toISOString();
      }
      return trimmed;
    }
    if (typeof value === "boolean") return value;
    return value;
  };

  const valuesEqual = (a, b) => {
    const na = normalizeForComparison(a);
    const nb = normalizeForComparison(b);
    if (na === nb) return true;
    if (na == null && nb == null) return true;
    return false;
  };

  const createOfflineMatcher = (fields) => (entry, row) => {
    if (!entry || !row) return false;
    const idMatches =
      entry.id != null &&
      !isTempIdentifier(entry.id) &&
      row.id != null &&
      `${entry.id}` === `${row.id}`;
    const fieldsMatch = fields.every((field) => valuesEqual(entry[field], row[field]));
    if (idMatches) return fieldsMatch;
    return fieldsMatch;
  };

  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && `${v}`.length > 0);

  const matchInputsRow = createOfflineMatcher([
    "cycleId",
    "facilityId",
    "hatchery",
    "dateIso",
    "timeOfDeliveryIso",
    "waybill",
    "totalBoxes",
    "birdsPerBox",
    "internalChickTemp",
    "cargoHoldTemp",
    "vehicleRH",
    "holdingAreaTemp",
    "boxStockDensity",
    "driverName",
    "transportReg",
    "tripStartIso",
    "tripTempStart",
    "tripTempEnd",
    "tripHumStart",
    "tripHumEnd",
  ]);

  const matchDocRow = (entry, row) => {
    if (!entry || !row) return false;
    const idMatches =
      entry.id != null &&
      !isTempIdentifier(entry.id) &&
      row.id != null &&
      `${entry.id}` === `${row.id}`;
    const breederMatches =
      normalizeBreederCode(entry.breederCode) === normalizeBreederCode(row.breederCode);
    const otherFieldsMatch = [
      "cycleId",
      "facilityId",
      "description",
      "chickWeight",
      "quantity",
      "parentAge",
      "batchDateIso",
      "timeOfPullingIso",
      "hatcherNo",
      "setterNo",
      "deformities",
    ].every((field) => valuesEqual(entry[field], row[field]));
    const fieldsMatch = breederMatches && otherFieldsMatch;
    if (idMatches) return fieldsMatch;
    return fieldsMatch;
  };

  const mapInputsApiRows = (data) => {
    const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    return list.map((row, index) => {
      const id = pick(row?.id, row?.ID, row?.hatcheryId, row?.HatcheryId, row?.hatchery_id, index);
      const cycleVal = pick(row?.cycleId, row?.CycleID, row?.cycleID, row?.id, row?.ID);
      const facilityVal = pick(
        row?.facilityId,
        row?.FacilityID,
        row?.facilityID,
        row?.broilerId,
        row?.BroilerID,
        row?.broilerID
      );
      const hatcheryVal = pick(row?.hatcheryName, row?.HatcheryName, row?.hatchery_name);
      const dateVal = pick(row?.date, row?.Date, row?.hatcheryDate);
      const deliveryVal = pick(row?.timeOfDelivery, row?.TimeOfDelivery, row?.deliveryTime);
      return {
        id: id != null ? String(id) : `input-${index}`,
        cycleId: cycleVal != null ? String(cycleVal) : null,
        facilityId: facilityVal != null ? String(facilityVal) : null,
        hatchery: hatcheryVal != null ? String(hatcheryVal) : "",
        dateIso: toIso(dateVal),
        dateInput: toDateInputValue(dateVal),
        waybill: pick(row?.invoiceNumber, row?.InvoiceNumber, row?.invoice_number) ?? "",
        timeOfDeliveryIso: toNaiveDateTime(deliveryVal),
        timeOfDeliveryInput: toInputDateTime(deliveryVal),
        totalBoxes:
          pick(row?.totalNoOfBoxes, row?.totalBoxes, row?.TotalNoOfBoxes, row?.total_boxes) ?? "",
        birdsPerBox: pick(row?.noOfBirdsInBoxes, row?.birdsPerBox, row?.NoOfBirdsInBoxes) ?? "",
        internalChickTemp: pick(row?.internalChickTemp, row?.InternalChickTemp) ?? "",
        cargoHoldTemp: pick(row?.cargoHoldTemp, row?.CargoHoldTemp) ?? "",
        vehicleRH:
          pick(row?.vehicleRelativeHumidity, row?.vehicleRH, row?.VehicleRelativeHumidity) ?? "",
        holdingAreaTemp: pick(row?.holdingAreaTemp, row?.HoldingAreaTemp) ?? "",
        boxStockDensity: pick(row?.boxStockDensity, row?.BoxStockDensity) ?? "",
        driverName: pick(row?.driverName, row?.DriverName) ?? "",
        transportReg: pick(row?.transportReg, row?.TransportReg) ?? "",
        tripStartIso: toNaiveDateTime(pick(row?.tripStart, row?.TripStart)),
        tripStartInput: toInputDateTime(pick(row?.tripStart, row?.TripStart)),
        tripTempStart: pick(row?.tripTempStart, row?.TripTempStart) ?? "",
        tripTempEnd: pick(row?.tripTempEnd, row?.TripTempEnd) ?? "",
        tripHumStart: pick(row?.tripHumStart, row?.TripHumStart) ?? "",
        tripHumEnd: pick(row?.tripHumEnd, row?.TripHumEnd) ?? "",
      };
    });
  };

  const mapDocApiRows = (data) => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.value)
      ? data.value
      : Array.isArray(data?.result)
      ? data.result
      : data?.id != null || data?.ID != null
      ? [data]
      : [];
    return list.map((row, index) => {
      const id = pick(row?.id, row?.ID, index);
      const cycleVal = pick(row?.cycleId, row?.CycleID, row?.cycleID, row?.CycleId, row?.Id);
      const facilityVal = pick(
        row?.broilerId,
        row?.BroilerID,
        row?.broilerID,
        row?.facilityId,
        row?.FacilityID,
        row?.facilityID
      );
      const timeVal = pick(row?.timeOfPulling, row?.TimeOfPulling);
      const batchDate = pick(row?.batchDate, row?.BatchDate);
      return {
        id: id != null ? String(id) : `doc-${index}`,
        cycleId: cycleVal != null ? String(cycleVal) : null,
        facilityId: facilityVal != null ? String(facilityVal) : null,
        batchDateIso: batchDate ?? null,
        batchDate: toDateInputValue(batchDate),
        batchDateInput: toInputDateTime(batchDate),
        breederCode:
          normalizeBreederCode(pick(row?.breederCode, row?.BreederCode, row?.breeder_code)) ?? "",
        description: pick(row?.description, row?.Description) ?? "",
        chickWeight: pick(row?.chickWeight, row?.ChickWeight) ?? "",
        quantity: pick(row?.qauntity, row?.quantity, row?.Quantity) ?? "",
        parentAge: pick(row?.parentAge, row?.ParentAge) ?? "",
        timeOfPullingIso: timeVal ?? null,
        timeOfPulling: toTimeHM(timeVal ?? ""),
        hatcherNo: pick(row?.hatcherno, row?.hatcherNo, row?.HatcherNo) ?? "",
        setterNo: pick(row?.setterno, row?.setterNo, row?.SetterNo) ?? "",
        deformities: pick(row?.deformaties, row?.deformities, row?.Deformities) ?? "",
      };
    });
  };

  const queuePayloadMatchesRecord = (namespace, payload, record) => {
    if (!payload || typeof payload !== "object" || !record) return false;
    if (record?.__deleted) {
      return valuesEqual(payload?.id ?? payload?.ID ?? payload?.Id, record.id);
    }

    switch (namespace) {
      case OFFLINE_NS_INPUTS: {
        return (
          valuesEqual(payload?.cycleId ?? payload?.CycleID ?? payload?.cycleID, record.cycleId) &&
          valuesEqual(
            payload?.broilerId ?? payload?.BroilerID ?? payload?.facilityId ?? payload?.FacilityID,
            record.facilityId
          ) &&
          valuesEqual(
            payload?.hatcheryName ?? payload?.hatchery ?? payload?.HatcheryName,
            record.hatchery
          ) &&
          valuesEqual(
            toIso(payload?.date ?? payload?.Date ?? payload?.hatcheryDate),
            record.dateIso
          ) &&
          valuesEqual(
            toNaiveDateTime(
              payload?.timeOfDelivery ?? payload?.TimeOfDelivery ?? payload?.deliveryTime
            ),
            record.timeOfDeliveryIso
          ) &&
          valuesEqual(payload?.invoiceNumber ?? payload?.InvoiceNumber, record.waybill) &&
          valuesEqual(payload?.totalNoOfBoxes ?? payload?.totalBoxes, record.totalBoxes) &&
          valuesEqual(payload?.noOfBirdsInBoxes ?? payload?.birdsPerBox, record.birdsPerBox) &&
          valuesEqual(payload?.internalChickTemp, record.internalChickTemp) &&
          valuesEqual(payload?.cargoHoldTemp, record.cargoHoldTemp) &&
          valuesEqual(payload?.vehicleRelativeHumidity ?? payload?.vehicleRH, record.vehicleRH) &&
          valuesEqual(payload?.holdingAreaTemp, record.holdingAreaTemp) &&
          valuesEqual(payload?.boxStockDensity, record.boxStockDensity) &&
          valuesEqual(payload?.driverName, record.driverName) &&
          valuesEqual(payload?.transportReg, record.transportReg) &&
          valuesEqual(toNaiveDateTime(payload?.tripStart), record.tripStartIso) &&
          valuesEqual(payload?.tripTempStart, record.tripTempStart) &&
          valuesEqual(payload?.tripTempEnd, record.tripTempEnd) &&
          valuesEqual(payload?.tripHumStart, record.tripHumStart) &&
          valuesEqual(payload?.tripHumEnd, record.tripHumEnd)
        );
      }
      case OFFLINE_NS_DOC: {
        const breederMatches =
          normalizeBreederCode(payload?.breederCode ?? payload?.BreederCode) ===
          normalizeBreederCode(record.breederCode);
        return (
          breederMatches &&
          valuesEqual(payload?.cycleId ?? payload?.CycleID ?? payload?.cycleID, record.cycleId) &&
          valuesEqual(
            payload?.broilerId ?? payload?.BroilerID ?? payload?.facilityId ?? payload?.FacilityID,
            record.facilityId
          ) &&
          valuesEqual(payload?.description ?? payload?.Description, record.description) &&
          valuesEqual(payload?.chickWeight ?? payload?.ChickWeight, record.chickWeight) &&
          valuesEqual(
            payload?.qauntity ?? payload?.quantity ?? payload?.Quantity,
            record.quantity
          ) &&
          valuesEqual(payload?.parentAge ?? payload?.ParentAge, record.parentAge) &&
          valuesEqual(payload?.batchDate ?? payload?.BatchDate, record.batchDateIso) &&
          valuesEqual(payload?.timeOfPulling ?? payload?.TimeOfPulling, record.timeOfPullingIso) &&
          valuesEqual(
            payload?.hatcherno ?? payload?.hatcherNo ?? payload?.HatcherNo,
            record.hatcherNo
          ) &&
          valuesEqual(
            payload?.setterno ?? payload?.setterNo ?? payload?.SetterNo,
            record.setterNo
          ) &&
          valuesEqual(
            payload?.deformaties ?? payload?.deformities ?? payload?.Deformities,
            record.deformities
          )
        );
      }
      default:
        return false;
    }
  };

  const updateQueuePayloadForRecord = (namespace, targetId, payload) => {
    if (targetId == null) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;
    const records = readOfflineRecords(namespace, facilityId, cycleId);
    if (!Array.isArray(records) || records.length === 0) return;
    const record = records.find((entry) => entry && `${entry.id}` === `${targetId}`);
    if (!record?.__queueId) return;
    const queueIndex = queue.findIndex((entry) => entry && `${entry.id}` === `${record.__queueId}`);
    if (queueIndex < 0) return;

    const entry = queue[queueIndex];
    if (!entry) return;
    const updatedEntry = {
      ...entry,
      timestamp: Date.now(),
      data: {
        type: "json",
        body: JSON.stringify(payload ?? null),
      },
    };
    const updatedQueue = [...queue];
    updatedQueue[queueIndex] = updatedEntry;
    writeOfflineQueue(updatedQueue);
  };

  const handleQueuedOfflineRequest = (namespace, targetId) => {
    if (targetId == null) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;

    const records = readOfflineRecords(namespace, facilityId, cycleId);
    if (!Array.isArray(records) || records.length === 0) return;
    const recordIndex = records.findIndex((entry) => entry && `${entry.id}` === `${targetId}`);
    if (recordIndex < 0) return;
    const record = records[recordIndex] || {};
    const existingQueueId = record.__queueId ?? null;

    const existingQueueIndex =
      existingQueueId != null
        ? queue.findIndex((entry) => entry && `${entry.id}` === `${existingQueueId}`)
        : -1;

    let matchedIndex = -1;
    for (let idx = queue.length - 1; idx >= 0; idx -= 1) {
      const payload = parseQueuePayload(queue[idx]);
      if (record?.__deleted) {
        if (valuesEqual(payload?.id ?? payload?.ID ?? payload?.Id, record.id)) {
          matchedIndex = idx;
          break;
        }
      } else if (queuePayloadMatchesRecord(namespace, payload, record)) {
        matchedIndex = idx;
        break;
      }
    }

    if (matchedIndex < 0) {
      matchedIndex = existingQueueIndex;
    }
    if (matchedIndex < 0 && queue.length) {
      matchedIndex = queue.length - 1;
    }

    if (matchedIndex < 0) {
      if (existingQueueId != null) {
        const nextRecords = [...records];
        nextRecords[recordIndex] = { ...record, __queueId: null };
        replaceOfflineRecords(namespace, facilityId, cycleId, nextRecords);
      }
      return;
    }

    const matchedEntry = queue[matchedIndex];
    if (!matchedEntry || matchedEntry.id == null) return;

    let updatedQueue = [...queue];
    let nextQueueId = existingQueueId;

    if (existingQueueIndex >= 0) {
      const existingEntry = updatedQueue[existingQueueIndex];
      if (!existingEntry) return;
      const mergedEntry = {
        ...existingEntry,
        url: matchedEntry.url ?? existingEntry.url,
        baseURL: matchedEntry.baseURL ?? existingEntry.baseURL,
        method: matchedEntry.method ?? existingEntry.method,
        headers: matchedEntry.headers ?? existingEntry.headers,
        params: matchedEntry.params ?? existingEntry.params,
        data: matchedEntry.data ?? existingEntry.data,
        timestamp: matchedEntry.timestamp ?? existingEntry.timestamp,
      };
      updatedQueue[existingQueueIndex] = mergedEntry;
      if (matchedIndex !== existingQueueIndex) {
        updatedQueue.splice(matchedIndex, 1);
        if (matchedIndex < existingQueueIndex) {
          // existing entry shifts left by one after removal
        }
      }
    } else {
      nextQueueId = matchedEntry.id;
      if (nextQueueId == null) return;
    }
    writeOfflineQueue(updatedQueue);

    const nextRecords = [...records];
    nextRecords[recordIndex] = { ...record, __queueId: nextQueueId };
    replaceOfflineRecords(namespace, facilityId, cycleId, nextRecords);
  };

  const buildInputsRowFromPayload = (payload, targetId) => ({
    id: targetId,
    cycleId: payload.cycleId != null ? String(payload.cycleId) : null,
    facilityId: payload.broilerId != null ? String(payload.broilerId) : null,
    hatchery: payload.hatcheryName != null ? String(payload.hatcheryName) : "",
    dateIso: payload.date ?? null,
    dateInput: toDateInputValue(payload.date),
    waybill: payload.invoiceNumber ?? "",
    timeOfDeliveryIso: payload.timeOfDelivery ?? null,
    timeOfDeliveryInput: toInputDateTime(payload.timeOfDelivery),
    totalBoxes: payload.totalNoOfBoxes != null ? String(payload.totalNoOfBoxes) : "",
    birdsPerBox: payload.noOfBirdsInBoxes != null ? String(payload.noOfBirdsInBoxes) : "",
    internalChickTemp: payload.internalChickTemp != null ? String(payload.internalChickTemp) : "",
    cargoHoldTemp: payload.cargoHoldTemp != null ? String(payload.cargoHoldTemp) : "",
    vehicleRH:
      payload.vehicleRelativeHumidity != null ? String(payload.vehicleRelativeHumidity) : "",
    holdingAreaTemp: payload.holdingAreaTemp != null ? String(payload.holdingAreaTemp) : "",
    boxStockDensity: payload.boxStockDensity != null ? String(payload.boxStockDensity) : "",
    driverName: payload.driverName ?? "",
    transportReg: payload.transportReg ?? "",
    tripStartIso: payload.tripStart ?? null,
    tripStartInput: toInputDateTime(payload.tripStart),
    tripTempStart: payload.tripTempStart != null ? String(payload.tripTempStart) : "",
    tripTempEnd: payload.tripTempEnd != null ? String(payload.tripTempEnd) : "",
    tripHumStart: payload.tripHumStart != null ? String(payload.tripHumStart) : "",
    tripHumEnd: payload.tripHumEnd != null ? String(payload.tripHumEnd) : "",
  });

  const buildDocRowFromPayload = (payload, targetId) => ({
    id: targetId,
    cycleId: payload.cycleId != null ? String(payload.cycleId) : null,
    facilityId: payload.broilerId != null ? String(payload.broilerId) : null,
    breederCode: normalizeBreederCode(payload.breederCode) ?? "",
    BreederCode: normalizeBreederCode(payload.breederCode) ?? "",
    description: payload.description ?? "",
    chickWeight: payload.chickWeight != null ? String(payload.chickWeight) : "",
    quantity: payload.qauntity != null ? String(payload.qauntity) : "",
    parentAge: payload.parentAge != null ? String(payload.parentAge) : "",
    batchDateIso: payload.batchDate ?? null,
    batchDate: toDateInputValue(payload.batchDate),
    batchDateInput: toInputDateTime(payload.batchDate),
    timeOfPullingIso: payload.timeOfPulling ?? null,
    timeOfPulling: toTimeHM(payload.timeOfPulling ?? ""),
    hatcherNo: payload.hatcherno != null ? String(payload.hatcherno) : "",
    setterNo: payload.setterno != null ? String(payload.setterno) : "",
    deformities: payload.deformaties != null ? String(payload.deformaties) : "",
  });

  const pushDocDisplayRow = (rowData, existingId) => {
    let inserted = false;
    let nextLength = 0;
    setDocRows((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const index = next.findIndex((row) => `${row.id}` === `${rowData.id}`);
      const displayRow = {
        id: rowData.id,
        breederCode: rowData.breederCode,
        description: rowData.description,
        chickWeight: rowData.chickWeight,
        quantity: rowData.quantity,
        parentAge: rowData.parentAge,
        batchDate: rowData.batchDate,
        batchDateInput: rowData.batchDateInput,
        batchDateIso: rowData.batchDateIso,
        timeOfPulling: rowData.timeOfPulling,
        timeOfPullingIso: rowData.timeOfPullingIso,
        hatcherNo: rowData.hatcherNo,
        setterNo: rowData.setterNo,
        deformities: rowData.deformities,
        cycleId: rowData.cycleId,
        facilityId: rowData.facilityId,
        __isOffline: rowData.__isOffline,
        __pendingCreate: rowData.__pendingCreate,
        __pendingUpdate: rowData.__pendingUpdate,
      };
      if (index >= 0) {
        next[index] = { ...next[index], ...displayRow };
      } else {
        next.unshift(displayRow);
        inserted = true;
      }
      nextLength = next.length;
      return next;
    });
    setDocPage((prevPage) => {
      if (!inserted) {
        return prevPage;
      }
      if (existingId != null && !isTempIdentifier(existingId)) {
        return prevPage;
      }
      if (nextLength <= 0) {
        return 1;
      }
      return 1;
    });
  };

  const getDisplayDocId = (row) => {
    if (!row) return "";
    const rawId = row.id;
    if (rawId == null) return "";
    if (row.__pendingCreate || row.__pendingUpdate || isTempIdentifier(rawId)) return "";
    return rawId;
  };
  const combineServerRowsWithOffline = (namespace, serverRows, matcher) => {
    const baseRows = Array.isArray(serverRows) ? serverRows : [];
    const storedOffline = readOfflineRecords(namespace, facilityId, cycleId);
    let offlineList = Array.isArray(storedOffline) ? storedOffline.filter(Boolean) : [];
    if (!offlineList.length) return baseRows;
    const queueEntries = readOfflineQueue();
    const queueIds = new Set(
      Array.isArray(queueEntries) ? queueEntries.filter(Boolean).map((entry) => `${entry.id}`) : []
    );

    const filteredOffline = offlineList.filter((entry) => {
      if (!entry?.__queueId) return true;
      return queueIds.has(`${entry.__queueId}`);
    });

    if (filteredOffline.length !== offlineList.length) {
      offlineList = filteredOffline;
      if (offlineList.length) {
        replaceOfflineRecords(namespace, facilityId, cycleId, offlineList);
      } else {
        clearOfflineRecords(namespace, facilityId, cycleId);
        return baseRows;
      }
    }

    const pending = offlineList.filter((entry) => {
      if (!entry) return false;
      if (entry.__deleted) {
        return baseRows.some((row) => row && `${row.id}` === `${entry.id}`);
      }
      const hasPersistedId = entry.id != null && !isTempIdentifier(entry.id);
      if (hasPersistedId) {
        const matchedById = baseRows.some((row) => row && `${row.id}` === `${entry.id}`);
        if (matchedById) {
          return false;
        }
      }
      return !baseRows.some((row) => matcher(entry, row));
    });

    if (pending.length === 0) {
      clearOfflineRecords(namespace, facilityId, cycleId);
      return baseRows;
    }

    if (pending.length !== offlineList.length) {
      replaceOfflineRecords(namespace, facilityId, cycleId, pending);
    }

    return mergeOfflineRecords(baseRows, pending);
  };

  const applyOptimisticInputsUpsert = (payload, existingId) => {
    const targetId = existingId != null ? existingId : makeTempId();
    const existingRecords = readOfflineRecords(OFFLINE_NS_INPUTS, facilityId, cycleId);
    const existingEntry =
      Array.isArray(existingRecords) &&
      existingRecords.find((entry) => entry && `${entry.id}` === `${targetId}`);
    const rowData = {
      ...buildInputsRowFromPayload(payload, targetId),
      __isOffline: true,
      __pendingCreate: existingId == null || isTempIdentifier(existingId),
      __pendingUpdate: existingId != null && !isTempIdentifier(existingId),
      __queueId: existingEntry?.__queueId ?? null,
    };
    upsertOfflineRecord(OFFLINE_NS_INPUTS, facilityId, cycleId, rowData);
    return { targetId, rowData };
  };

  const applyOptimisticDocUpsert = (payload, existingId) => {
    const targetId = existingId != null ? existingId : makeTempId();
    const existingRecords = readOfflineRecords(OFFLINE_NS_DOC, facilityId, cycleId);
    const existingEntry =
      Array.isArray(existingRecords) &&
      existingRecords.find((entry) => entry && `${entry.id}` === `${targetId}`);
    const rowData = {
      ...buildDocRowFromPayload(payload, targetId),
      __isOffline: true,
      __pendingCreate: existingId == null || isTempIdentifier(existingId),
      __pendingUpdate: existingId != null && !isTempIdentifier(existingId),
      __queueId: existingEntry?.__queueId ?? null,
    };
    upsertOfflineRecord(OFFLINE_NS_DOC, facilityId, cycleId, rowData);
    pushDocDisplayRow(rowData, existingId);
    return { targetId, rowData };
  };

  const applyOptimisticDocDelete = (row) => {
    if (!row) return;
    const idString = `${row.id}`;
    setDocRows((prev) =>
      Array.isArray(prev) ? prev.filter((entry) => `${entry.id}` !== idString) : []
    );
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      if (row?.__queueId) {
        removeQueueEntry(row.__queueId);
      }
      removeOfflineRecord(OFFLINE_NS_DOC, facilityId, cycleId, row.id);
    } else {
      upsertOfflineRecord(OFFLINE_NS_DOC, facilityId, cycleId, {
        id: row.id,
        __deleted: true,
        __isOffline: true,
        __queueId: row?.__queueId ?? null,
      });
    }
  };

  const applyDocRowsState = (rows) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    setDocRows(safeRows);
  };

  const openDocEditDialog = (row) => {
    if (!row) return;
    setDocEditId(row.id ?? null);
    setDocEditInitial({
      breederCode: row.breederCode ?? "",
      description: row.description ?? "",
      chickWeight: row.chickWeight ?? "",
      quantity: row.quantity ?? "",
      parentAge: row.parentAge ?? "",
      batchDate: toInputDateTime(row.batchDateIso ?? row.batchDateInput ?? row.batchDate ?? ""),
      timeOfPullingIso: row.timeOfPullingIso ?? null,
      hatcherNo: row.hatcherNo ?? "",
      setterNo: row.setterNo ?? "",
      deformities: row.deformities ?? "",
    });
    setDocEditOpen(true);
  };

  const createDocDeletePayload = (row) => {
    if (!row) return null;
    const breederCode = normalizeBreederCode(row.breederCode);
    return {
      id: toNumber(row.id) ?? row.id,
      cycleId: toNumber(row.cycleId) ?? toNumber(cycleId) ?? cycleId,
      breederCode,
      BreederCode: breederCode,
      description: row.description ?? null,
      chickWeight: toNumber(row.chickWeight),
      qauntity: toNumber(row.quantity),
      parentAge: toNumber(row.parentAge),
      batchDate: row.batchDateIso ?? toBatchDateTime(row.batchDate),
      timeOfPulling: row.timeOfPullingIso ?? null,
      hatcherno: toNumber(row.hatcherNo),
      setterno: toNumber(row.setterNo),
      broilerId: toNumber(row.facilityId) ?? toNumber(facilityId) ?? facilityId,
      deformaties: toNumber(row.deformities),
    };
  };

  const applyInputsState = (row) => {
    if (!row) {
      setExistingId(null);
      setHatchery("");
      setDate("");
      setWaybill("");
      setTimeOfDelivery("");
      setTotalBoxes("");
      setBirdsPerBox("");
      setInternalChickTemp("");
      setCargoHoldTemp("");
      setVehicleRH("");
      setHoldingAreaTemp("");
      setBoxStockDensity("");
      setDriverName("");
      setTransportReg("");
      setStartTime("");
      setTripTempStart("");
      setTripTempEnd("");
      setTripHumStart("");
      setTripHumEnd("");
      return;
    }
    setExistingId(row.id ?? null);
    setHatchery(row.hatchery != null ? String(row.hatchery) : "");
    setDate(row.dateInput ?? "");
    setWaybill(row.waybill ?? "");
    setTimeOfDelivery(row.timeOfDeliveryInput ?? "");
    setTotalBoxes(row.totalBoxes ?? "");
    setBirdsPerBox(row.birdsPerBox ?? "");
    setInternalChickTemp(row.internalChickTemp ?? "");
    setCargoHoldTemp(row.cargoHoldTemp ?? "");
    setVehicleRH(row.vehicleRH ?? "");
    setHoldingAreaTemp(row.holdingAreaTemp ?? "");
    setBoxStockDensity(row.boxStockDensity ?? "");
    setDriverName(row.driverName ?? "");
    setTransportReg(row.transportReg ?? "");
    setStartTime(row.tripStartInput ?? "");
    setTripTempStart(row.tripTempStart ?? "");
    setTripTempEnd(row.tripTempEnd ?? "");
    setTripHumStart(row.tripHumStart ?? "");
    setTripHumEnd(row.tripHumEnd ?? "");
  };

  const deriveHatcheryMap = (farmsData) => {
    const list = Array.isArray(farmsData) ? farmsData : [];
    return list.reduce((acc, item) => {
      const sectorType = item?.sectorType ?? item?.SectorType;
      if (String(sectorType) !== "4") return acc;
      const id = item?.id ?? item?.ID;
      const name = item?.name ?? item?.Name;
      if (id != null && name != null) acc[String(id)] = String(name);
      return acc;
    }, {});
  };

  const toDateInputValue = (val) => {
    if (!val) return "";
    const dateOnlyMatch = String(val).match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateOnlyMatch) return dateOnlyMatch[1];
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const applyHatcheryOptions = (rows, dictMap) => {
    const list = Array.isArray(rows) ? rows : [];
    const idsFromRows = list
      .map((r) => (r?.hatchery == null ? "" : String(r.hatchery)))
      .filter((s) => s.length > 0);
    const dictIds = dictMap && typeof dictMap === "object" ? Object.keys(dictMap) : [];
    const ids = Array.from(new Set([...dictIds, ...idsFromRows]));
    setHatcheryOptions(ids);
    if (dictMap && Object.keys(dictMap).length > 0) {
      setHatcheryMap(dictMap);
    }
  };

  // Transport details
  const [internalChickTemp, setInternalChickTemp] = useState("");
  const [cargoHoldTemp, setCargoHoldTemp] = useState("");
  const [vehicleRH, setVehicleRH] = useState("");
  const [holdingAreaTemp, setHoldingAreaTemp] = useState("");
  const [boxStockDensity, setBoxStockDensity] = useState("");
  const [driverName, setDriverName] = useState("");
  const [transportReg, setTransportReg] = useState("");
  const [startTime, setStartTime] = useState("");
  const [tripTempStart, setTripTempStart] = useState("");
  const [tripTempEnd, setTripTempEnd] = useState("");
  const [tripHumStart, setTripHumStart] = useState("");
  const [tripHumEnd, setTripHumEnd] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const showSuccess = (message) => setSnack({ open: true, message, severity: "success" });
  const showInfo = (message) => setSnack({ open: true, message, severity: "info" });
  const showError = (message) => setSnack({ open: true, message, severity: "error" });
  const hasInputsFormData = () => {
    if (existingId != null) return true;
    return [
      hatchery,
      date,
      waybill,
      timeOfDelivery,
      totalBoxes,
      birdsPerBox,
      internalChickTemp,
      cargoHoldTemp,
      vehicleRH,
      holdingAreaTemp,
      boxStockDensity,
      driverName,
      transportReg,
      startTime,
      tripTempStart,
      tripTempEnd,
      tripHumStart,
      tripHumEnd,
    ].some((val) => val !== "" && val !== null && val !== undefined);
  };

  const toBatchDateTime = (value) => {
    return toNaiveDateTime(value);
  };

  const addDocRow = async (row) => {
    const payload = {
      cycleId: toNumber(cycleId) ?? cycleId,
      farmId: toNumber(farmId) ?? farmId,
      invoiceNumber: String(waybill ?? "").trim() || null,
      breederCode: normalizeBreederCode(row?.breederCode),
      BreederCode: normalizeBreederCode(row?.breederCode),
      description: row?.description ?? null,
      chickWeight: toNumber(row?.chickWeight),
      qauntity: toNumber(row?.quantity),
      parentAge: toNumber(row?.parentAge),
      batchDate: toBatchDateTime(row?.batchDate),
      timeOfPulling: null,
      hatcherno: toNumber(row?.hatcherNo),
      setterno: toNumber(row?.setterNo),
      broilerId: toNumber(facilityId) ?? facilityId,
      deformaties: toNumber(row?.deformities),
    };

    let response;
    try {
      response = await axios.post(updateDOCTableUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const { targetId } = applyOptimisticDocUpsert(payload, null);
        handleQueuedOfflineRequest(OFFLINE_NS_DOC, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_DOC, targetId, payload);
        setDocDialogOpen(false);
        showInfo("DOC row saved locally. It will sync when you're online.");
        return;
      }
      console.error("Failed to save DOC row", error);
      showError("Failed to save DOC row");
      return;
    }

    if (wasQueuedOffline(response)) {
      const { targetId } = applyOptimisticDocUpsert(payload, null);
      handleQueuedOfflineRequest(OFFLINE_NS_DOC, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_DOC, targetId, payload);
      setDocDialogOpen(false);
      showInfo("DOC row saved locally. It will sync when you're online.");
      return;
    }

    setDocDialogOpen(false);
    showSuccess("DOC row added successfully");
    if (selectedHatcheryDataId === "new") {
      const createdId =
        response?.data?.id ?? response?.data?.ID ?? response?.data?.data?.id ?? makeTempId();
      pushDocDisplayRow(buildDocRowFromPayload(payload, createdId), null);
    } else {
      setOfflineTick((tick) => tick + 1);
    }
    console.log("DOC row saved", payload);
  };

  // Open edit dialog for the first row on the current page
  const handleOpenEdit = async () => {
    const current = Array.isArray(docRows) && docRows.length ? docRows[0] : null;
    const id = current?.id;
    if (!id) return;

    if (current.__isOffline || isTempIdentifier(id)) {
      openDocEditDialog(current);
      return;
    }

    try {
      const res = await axios.get(docDataUrl(facilityId, cycleId, selectedInvoiceNumber));
      const data = Array.isArray(res.data) ? res.data : [];
      const target = data.find((r) => String(r?.id ?? r?.ID) === String(id));
      if (target) {
        const mapped = mapDocApiRows([target])[0];
        if (mapped) {
          openDocEditDialog(mapped);
          return;
        }
      }
    } catch (error) {
      // ignore fetch failures and fall back to cached row
    }

    openDocEditDialog(current);
  };

  // Edit a specific DOC row by id (opens dialog with fetched values)
  const handleOpenEditForRow = async (rowId) => {
    if (!rowId) return;

    const localRow = Array.isArray(docRows)
      ? docRows.find((entry) => String(entry?.id) === String(rowId))
      : null;

    if (localRow && (localRow.__isOffline || isTempIdentifier(localRow.id))) {
      openDocEditDialog(localRow);
      return;
    }

    try {
      const res = await axios.get(docDataUrl(facilityId, cycleId, selectedInvoiceNumber));
      const data = Array.isArray(res.data) ? res.data : [];
      const target = data.find((r) => String(r?.id ?? r?.ID) === String(rowId));
      if (target) {
        const mapped = mapDocApiRows([target])[0];
        if (mapped) {
          openDocEditDialog(mapped);
          return;
        }
      }
    } catch (error) {
      // ignore fetch failures
    }

    if (localRow) {
      openDocEditDialog(localRow);
    }
  };

  // Save edits for an existing DOC row (port 88 API)
  const handleSaveEdit = async (form) => {
    if (!docEditId) return;

    const payload = {
      id: toNumber(docEditId) ?? docEditId,
      cycleId: toNumber(cycleId) ?? cycleId,
      farmId: toNumber(farmId) ?? farmId,
      invoiceNumber: String(waybill ?? "").trim() || null,
      breederCode: normalizeBreederCode(form?.breederCode),
      BreederCode: normalizeBreederCode(form?.breederCode),
      description: form?.description ?? null,
      chickWeight: toNumber(form?.chickWeight),
      qauntity: toNumber(form?.quantity),
      parentAge: toNumber(form?.parentAge),
      batchDate: toBatchDateTime(form?.batchDate),
      timeOfPulling: form?.timeOfPullingIso ?? null,
      hatcherno: toNumber(form?.hatcherNo),
      setterno: toNumber(form?.setterNo),
      broilerId: toNumber(facilityId) ?? facilityId,
      deformaties: toNumber(form?.deformities),
    };

    let response;
    try {
      response = await axios.post(updateDOCTableUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const { targetId } = applyOptimisticDocUpsert(payload, docEditId);
        handleQueuedOfflineRequest(OFFLINE_NS_DOC, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_DOC, targetId, payload);
        setDocEditOpen(false);
        setDocEditId(null);
        setDocEditInitial(null);
        showInfo("DOC row saved locally. It will sync when you're online.");
        return;
      }
      console.error("Failed to update DOC row", error);
      showError("Failed to update DOC row");
      return;
    }

    if (wasQueuedOffline(response)) {
      const { targetId } = applyOptimisticDocUpsert(payload, docEditId);
      handleQueuedOfflineRequest(OFFLINE_NS_DOC, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_DOC, targetId, payload);
      setDocEditOpen(false);
      setDocEditId(null);
      setDocEditInitial(null);
      showInfo("DOC row saved locally. It will sync when you're online.");
      return;
    }

    setDocEditOpen(false);
    setDocEditId(null);
    setDocEditInitial(null);
    showSuccess("DOC row updated successfully");
    if (selectedHatcheryDataId === "new") {
      pushDocDisplayRow(buildDocRowFromPayload(payload, docEditId), docEditId);
    } else {
      setOfflineTick((tick) => tick + 1);
    }
    console.log("DOC row updated", payload);
  };

  // Delete first visible row on the current page with confirmation and server refresh
  const handleDelete = async () => {
    const current = Array.isArray(docRows) && docRows.length ? docRows[0] : null;
    const id = current?.id;
    if (!current || !id) return;

    if (current.__pendingCreate || isTempIdentifier(id)) {
      applyOptimisticDocDelete(current);
      showInfo("DOC row removed locally.");
      return;
    }

    const ok = window.confirm("Delete this DOC row? This cannot be undone.");
    if (!ok) return;

    let payload = createDocDeletePayload(current);
    if (!payload) return;

    let response;
    try {
      response = await axios.post(removeDOCTableUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        applyOptimisticDocDelete(current);
        handleQueuedOfflineRequest(OFFLINE_NS_DOC, current.id);
        updateQueuePayloadForRecord(OFFLINE_NS_DOC, current.id, payload);
        showInfo("DOC row deletion saved locally. It will sync when you're online.");
        return;
      }
      console.error("Failed to delete DOC row", error);
      showError("Failed to delete DOC row");
      return;
    }

    if (wasQueuedOffline(response)) {
      applyOptimisticDocDelete(current);
      handleQueuedOfflineRequest(OFFLINE_NS_DOC, current.id);
      updateQueuePayloadForRecord(OFFLINE_NS_DOC, current.id, payload);
      showInfo("DOC row deletion saved locally. It will sync when you're online.");
      return;
    }

    showSuccess("DOC row deleted successfully");
    if (selectedHatcheryDataId === "new") {
      setDocRows((rows) => rows.filter((entry) => String(entry.id) !== String(id)));
    } else {
      setOfflineTick((tick) => tick + 1);
    }
  };

  // Delete a specific DOC row by id
  const handleDeleteForRow = async (rowId) => {
    if (!rowId) return;

    const row = Array.isArray(docRows)
      ? docRows.find((entry) => String(entry?.id) === String(rowId))
      : null;
    if (!row) return;

    if (row.__pendingCreate || isTempIdentifier(row.id)) {
      applyOptimisticDocDelete(row);
      showInfo("DOC row removed locally.");
      return;
    }

    const ok = window.confirm("Delete this DOC row? This cannot be undone.");
    if (!ok) return;

    let payload = createDocDeletePayload(row);

    if (!payload || payload.broilerId == null || payload.cycleId == null) {
      try {
        const res = await axios.get(docDataUrl(facilityId, cycleId, selectedInvoiceNumber));
        const data = Array.isArray(res.data) ? res.data : [];
        const target = data.find((r) => String(r?.id ?? r?.ID) === String(rowId));
        if (target) {
          const mapped = mapDocApiRows([target])[0];
          const rebuilt = createDocDeletePayload(mapped);
          if (rebuilt) payload = rebuilt;
        }
      } catch (error) {
        // ignore fetch failures; fall back to existing payload
      }
    }

    if (!payload) return;

    let response;
    try {
      response = await axios.post(removeDOCTableUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        applyOptimisticDocDelete(row);
        handleQueuedOfflineRequest(OFFLINE_NS_DOC, row.id);
        updateQueuePayloadForRecord(OFFLINE_NS_DOC, row.id, payload);
        showInfo("DOC row deletion saved locally. It will sync when you're online.");
        return;
      }
      console.error("Failed to delete DOC row", error);
      showError("Failed to delete DOC row");
      return;
    }

    if (wasQueuedOffline(response)) {
      applyOptimisticDocDelete(row);
      handleQueuedOfflineRequest(OFFLINE_NS_DOC, row.id);
      updateQueuePayloadForRecord(OFFLINE_NS_DOC, row.id, payload);
      showInfo("DOC row deletion saved locally. It will sync when you're online.");
      return;
    }

    showSuccess("DOC row deleted successfully");
    if (selectedHatcheryDataId === "new") {
      setDocRows((current) => current.filter((entry) => String(entry.id) !== String(rowId)));
    } else {
      setOfflineTick((tick) => tick + 1);
    }
  };

  // Helpers
  const toInputDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const toInputDateTime = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };
  const toIso = (val) => {
    if (!val) return null;
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };
  const toTimeHM = (val) => {
    if (!val) return "";
    const s = String(val);
    // Already in HH:mm or HH:mm:ss
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 5);
    const d = new Date(s);
    if (isNaN(d.getTime())) return s.slice(11, 16) || "";
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mi}`;
  };
  // Preserve local datetime from a datetime-local input without timezone conversion
  const toNaiveDateTime = (val) => {
    if (!val) return null;
    // Expect formats like YYYY-MM-DDTHH:mm or YYYY-MM-DDTHH:mm:ss
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(val)) return `${val}:00`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(val)) return val;
    // Fallback: try Date parse but return naive string in local time
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
  };
  const toNumber = (val) => {
    if (val === "" || val === null || val === undefined) return null;
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  };
  const normalizeBreederCode = (val) => {
    if (val === undefined || val === null) return null;
    const str = String(val).trim();
    if (!str) return null;
    return str.slice(0, 10);
  };

  // Load hatchery options for the selected facility/cycle and map IDs to descriptions
  useEffect(() => {
    const { facilityId: selectedFacilityId, cycleId: selectedCycleId } = selectionKeys;
    if (selectedFacilityId == null || selectedCycleId == null) {
      applyInputsState(null);
      applyDocRowsState([]);
      setHatcheryOptions([]);
      setHatcheryMap({});
      return;
    }

    let cancelled = false;
    const facilityIdStr = String(selectedFacilityId);
    const cycleIdStr = String(selectedCycleId);

    const applyFromSources = (inputsSource, docSource, dictSource) => {
      if (cancelled) return;

      const contextChanged =
        prevSelectionRef.current.facilityId !== selectedFacilityId ||
        prevSelectionRef.current.cycleId !== selectedCycleId ||
        prevSelectionRef.current.selectedHatcheryDataId !== selectedHatcheryDataId;

      const inputsRows = mapInputsApiRows(inputsSource).filter(
        (row) =>
          `${row.cycleId}` === cycleIdStr &&
          (row.facilityId == null || `${row.facilityId}` === facilityIdStr)
      );
      const mergedInputs = combineServerRowsWithOffline(
        OFFLINE_NS_INPUTS,
        inputsRows,
        matchInputsRow
      );
      const activeInputs = mergedInputs.filter(
        (row) =>
          `${row.cycleId}` === cycleIdStr &&
          (row.facilityId == null || `${row.facilityId}` === facilityIdStr)
      );
      const shouldUpdateInputs = contextChanged || (!docDialogOpen && !hasInputsFormData());
      if (shouldUpdateInputs) {
        const selectedInput =
          selectedHatcheryDataId === "new"
            ? null
            : selectedHatcheryDataId == null
            ? activeInputs[0] ?? null
            : activeInputs.find((row) => String(row.id) === String(selectedHatcheryDataId)) ?? null;
        applyInputsState(selectedInput);
      }
      prevSelectionRef.current = {
        facilityId: selectedFacilityId,
        cycleId: selectedCycleId,
        selectedHatcheryDataId,
      };

      const dictMap = deriveHatcheryMap(dictSource?.data ?? dictSource);
      applyHatcheryOptions(activeInputs, dictMap);
      if (!dictMap || Object.keys(dictMap).length === 0) {
        setHatcheryMap({});
      }

      if (selectedHatcheryDataId === "new") {
        if (contextChanged) applyDocRowsState([]);
      } else {
        const docRows = mapDocApiRows(docSource).filter(
          (row) =>
            (row.cycleId == null || `${row.cycleId}` === cycleIdStr) &&
            (row.facilityId == null || `${row.facilityId}` === facilityIdStr)
        );
        const mergedDocs = selectedInvoiceNumber
          ? docRows
          : combineServerRowsWithOffline(OFFLINE_NS_DOC, docRows, matchDocRow);
        const activeDocs = mergedDocs.filter(
          (row) =>
            (row.cycleId == null || `${row.cycleId}` === cycleIdStr) &&
            (row.facilityId == null || `${row.facilityId}` === facilityIdStr)
        );
        applyDocRowsState(activeDocs);
      }
    };

    const hydrateFromCache = () => {
      const cachedInputs = readCachedCollection(
        hatcheryDataUrl(selectedFacilityId, selectedCycleId)
      );
      const cachedDocs =
        selectedHatcheryDataId === "new"
          ? []
          : readCachedCollection(
              docDataUrl(selectedFacilityId, selectedCycleId, selectedInvoiceNumber)
            );
      const cachedDict = readCachedCollection(allFarmsUrl());
      applyFromSources(cachedInputs ?? [], cachedDocs ?? [], cachedDict ?? []);
    };

    const load = async () => {
      const offline = typeof navigator !== "undefined" && navigator && navigator.onLine === false;
      if (offline) {
        hydrateFromCache();
        return;
      }

      try {
        const [inputsRes, dictRes, docRes] = await Promise.all([
          axios.get(hatcheryDataUrl(selectedFacilityId, selectedCycleId)),
          axios.get(allFarmsUrl()),
          selectedHatcheryDataId === "new"
            ? Promise.resolve({ data: [] })
            : axios.get(docDataUrl(selectedFacilityId, selectedCycleId, selectedInvoiceNumber)),
        ]);
        if (cancelled) return;
        applyFromSources(inputsRes?.data, docRes?.data, dictRes?.data);
      } catch (error) {
        if (cancelled) return;
        hydrateFromCache();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectionKeys]);
  // Expose save() to parent via ref
  useImperativeHandle(ref, () => ({ save: handleSave }));

  async function handleSave() {
    const payload = {
      broilerId: toNumber(facilityId) ?? facilityId,
      cycleId: toNumber(cycleId) ?? cycleId,
      hatcheryName: toNumber(hatchery) ?? hatchery,
      date: toIso(date),
      invoiceNumber: (toNumber(waybill) ?? waybill) || null,
      totalNoOfBoxes: toNumber(totalBoxes),
      noOfBirdsInBoxes: toNumber(birdsPerBox),
      internalChickTemp: toNumber(internalChickTemp),
      vehicleRelativeHumidity: toNumber(vehicleRH),
      cargoHoldTemp: toNumber(cargoHoldTemp),
      boxStockDensity: toNumber(boxStockDensity),
      holdingAreaTemp: toNumber(holdingAreaTemp),
      driverName: driverName.trim() || null,
      transportReg: transportReg.trim() || null,
      tripStart: toNaiveDateTime(startTime),
      tripTempStart: toNumber(tripTempStart),
      tripTempEnd: toNumber(tripTempEnd),
      tripHumStart: toNumber(tripHumStart),
      tripHumEnd: toNumber(tripHumEnd),
      timeOfDelivery: toNaiveDateTime(timeOfDelivery),
    };
    if (existingId != null) payload.id = toNumber(existingId) ?? existingId;

    let response;
    try {
      response = await axios.post(updateHatcheryDataUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const { targetId, rowData } = applyOptimisticInputsUpsert(payload, existingId);
        applyInputsState(rowData);
        handleQueuedOfflineRequest(OFFLINE_NS_INPUTS, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_INPUTS, targetId, payload);
        showInfo("Inputs saved locally. They will sync when you're online.");
        return true;
      }
      console.error("Failed to save hatchery data", error);
      showError("Failed to save inputs");
      return false;
    }

    if (wasQueuedOffline(response)) {
      const { targetId, rowData } = applyOptimisticInputsUpsert(payload, existingId);
      applyInputsState(rowData);
      handleQueuedOfflineRequest(OFFLINE_NS_INPUTS, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_INPUTS, targetId, payload);
      showInfo("Inputs saved locally. They will sync when you're online.");
      return true;
    }

    console.log("Hatchery data saved", payload);
    showSuccess("Inputs saved successfully");
    setOfflineTick((tick) => tick + 1);
    return true;
  }

  return (
    <Box>
      {/* Header row */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Grid item xs="auto">
              <Typography sx={{ fontSize: "1rem", whiteSpace: "nowrap" }}>
                Hatchery name:
              </Typography>
            </Grid>
            <Grid item xs>
              <Select
                size="small"
                variant="outlined"
                fullWidth
                sx={{
                  width: { xs: "100%", md: "50%" },
                  "& .MuiSelect-select": { py: 1.35, fontSize: "1rem" },
                  "& .MuiOutlinedInput-input": { py: 1.35, fontSize: "1rem" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { "& .MuiMenuItem-root": { fontSize: "1rem" } },
                  },
                }}
                value={hatchery}
                onChange={(e) => setHatchery(String(e.target.value))}
                renderValue={(value) => hatcheryMap[String(value)] || value || "Select..."}
              >
                {hatcheryOptions.length === 0 ? (
                  <MenuItem value="">Select...</MenuItem>
                ) : (
                  hatcheryOptions.map((id) => (
                    <MenuItem key={id} value={id}>
                      {hatcheryMap[String(id)] || String(id)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Grid item xs="auto">
              <Typography sx={{ fontSize: "1rem", whiteSpace: "nowrap" }}>
                {" "}
                Invoice Date:{" "}
              </Typography>
            </Grid>
            <Grid item xs>
              <TextField
                type="date"
                size="medium"
                fullWidth
                sx={{ width: { xs: "100%", md: "35%" } }}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Delivery Details_ */}
      <SectionTitle>Delivery Details</SectionTitle>
      <Paper elevation={0} sx={{ p: 2 }}>
        <Grid container spacing={2} sx={{ width: { xs: "100%", md: "50%" } }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Invoice No."
              size="small"
              fullWidth
              value={waybill}
              onChange={(e) => setWaybill(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Delivery Date"
              type="datetime-local"
              size="medium"
              fullWidth
              inputRef={deliveryPickerRef}
              value={timeOfDelivery}
              onChange={(e) => setTimeOfDelivery(e.target.value)}
              onClick={() => deliveryPickerRef.current?.showPicker?.()}
              sx={
                timeOfDelivery
                  ? undefined
                  : { "& input::-webkit-datetime-edit": { color: "transparent" } }
              }
              InputLabelProps={{ shrink: true, sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Total no. of boxes"
              type="number"
              size="medium"
              fullWidth
              value={totalBoxes}
              onChange={(e) => setTotalBoxes(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Total birds per box"
              type="number"
              size="medium"
              fullWidth
              value={birdsPerBox}
              onChange={(e) => setBirdsPerBox(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* DOC Details */}
      <SectionTitle>DOC Details</SectionTitle>
      <Paper elevation={0} sx={{ pb: "0.3cm", borderRadius: 0 }}>
        {false && (
          <TableContainer
            component={Box}
            sx={{ overflowX: "auto", boxShadow: "none", borderRadius: 0 }}
          >
            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
                width: "25.92cm",
                minWidth: "25.92cm",
                borderCollapse: "collapse",
                "& th, & td": {
                  border: "1px solid",
                  borderColor: "grey.300",
                  fontFamily: "inherit",
                  fontSize: "0.8rem",
                  lineHeight: 1.2,
                  p: 0.75,
                  textAlign: "center",
                },
                "& thead th": {
                  bgcolor: "grey.100",
                  fontWeight: 400,
                  borderBottomWidth: "2px",
                },
              }}
            >
              <colgroup>
                <col style={{ width: "2.88cm" }} />
                <col style={{ width: "2.88cm" }} />
                <col style={{ width: "2.88cm" }} />
                <col style={{ width: "2.88cm" }} />
                <col style={{ width: "2.88cm" }} />
                <col style={{ width: "2.88cm" }} />
                <col style={{ width: "2.88cm" }} />
                <col style={{ width: "2.88cm" }} />
                <col style={{ width: "2.88cm" }} />
              </colgroup>
              <TableHead>
                <TableRow sx={{ height: "1.5cm" }}>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    Breeder code
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    Descript.
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    Chick weight
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    Quantity
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    Parent age (wk)
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    Time of pulling
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    Hatcher no.
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    Setter no.
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 400, textAlign: "center" }}>
                    No. of Deformities
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {docRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  docRows.map((r, idx) => (
                    <TableRow key={r.id ?? idx}>
                      <TableCell align="center">{r.breederCode}</TableCell>
                      <TableCell align="center">{r.description}</TableCell>
                      <TableCell align="center">{r.chickWeight}</TableCell>
                      <TableCell align="center">{r.quantity}</TableCell>
                      <TableCell align="center">{r.parentAge}</TableCell>
                      <TableCell align="center">{r.timeOfPulling}</TableCell>
                      <TableCell align="center">{r.hatcherNo}</TableCell>
                      <TableCell align="center">{r.setterNo}</TableCell>
                      <TableCell align="center">{r.deformities}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* Simple 9x2 table for alignment verification */}
        <Box
          sx={{
            mt: "0.3cm",
            ml: "0.5cm",
            width: "calc(100% - 0.5cm)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            gap: "0.2cm",
            overflow: "hidden",
          }}
        >
          <TableContainer component={Box} sx={{ boxShadow: "none" }}>
            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
                width: "24.408cm",
                minWidth: "24.408cm",
                borderCollapse: "collapse",
                "& th, & td": {
                  border: "1px solid",
                  borderColor: "grey.300",
                  fontSize: "0.8rem",
                  lineHeight: 1.2,
                  p: 0.75,
                  textAlign: "center",
                },
              }}
            >
              <colgroup>
                <col style={{ width: "1.368cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "2.304cm" }} />
                <col style={{ width: "1.8432cm" }} />
              </colgroup>
              <TableBody>
                <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Breeder code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descript.</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Chick weight</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Parent age (wk)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Time of pulling</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Hatcher no.</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Setter no.</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>No. of Deformities</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <IconButton
                      size="small"
                      onClick={() => setDocDialogOpen(true)}
                      aria-label="add"
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.800",
                        "&:hover": { bgcolor: "grey.200" },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {docRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 2, color: "text.secondary" }}>
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  docRows.map((r, idx) => (
                    <TableRow key={r.id ?? idx} sx={{ "& td": { fontSize: "0.85rem" } }}>
                      <TableCell>{getDisplayDocId(r)}</TableCell>
                      <TableCell>{r.batchDate ?? ""}</TableCell>
                      <TableCell>{r.breederCode ?? ""}</TableCell>
                      <TableCell>{r.description ?? ""}</TableCell>
                      <TableCell>{r.chickWeight ?? ""}</TableCell>
                      <TableCell>{r.quantity ?? ""}</TableCell>
                      <TableCell>{r.parentAge ?? ""}</TableCell>
                      <TableCell>{r.timeOfPulling ?? ""}</TableCell>
                      <TableCell>{r.hatcherNo ?? ""}</TableCell>
                      <TableCell>{r.setterNo ?? ""}</TableCell>
                      <TableCell>{r.deformities ?? ""}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <EditIcon
                            onClick={() => handleOpenEditForRow(r.id)}
                            sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                          />
                          <DeleteIcon
                            onClick={() => handleDeleteForRow(r.id)}
                            sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Removed external action icons column; actions now live inside the table */}
        </Box>
      </Paper>

      {/* Transport Details */}
      <SectionTitle>Transport Details</SectionTitle>
      <Paper elevation={0} sx={{ p: 2 }}>
        <Grid container spacing={2} sx={{ width: { xs: "100%", md: "50%" } }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Start Time"
              type="datetime-local"
              size="medium"
              fullWidth
              inputRef={startPickerRef}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              onClick={() => startPickerRef.current?.showPicker?.()}
              sx={
                startTime
                  ? undefined
                  : { "& input::-webkit-datetime-edit": { color: "transparent" } }
              }
              InputLabelProps={{ shrink: true, sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={false} md={6} sx={{ display: { xs: "none", md: "block" } }} />
          <Grid item xs={12} md={6}>
            <TextField
              label="Driver Name"
              size="medium"
              fullWidth
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Vehicle Registration"
              size="medium"
              fullWidth
              value={transportReg}
              onChange={(e) => setTransportReg(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Temp Start"
              type="number"
              size="medium"
              fullWidth
              value={tripTempStart}
              onChange={(e) => setTripTempStart(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Temp End"
              type="number"
              size="medium"
              fullWidth
              value={tripTempEnd}
              onChange={(e) => setTripTempEnd(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Humidity Start"
              type="number"
              size="medium"
              fullWidth
              value={tripHumStart}
              onChange={(e) => setTripHumStart(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Humidity End"
              type="number"
              size="medium"
              fullWidth
              value={tripHumEnd}
              onChange={(e) => setTripHumEnd(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.95rem" } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Add Row Dialog */}
      <DocDetailsDialog
        open={docDialogOpen}
        onClose={() => setDocDialogOpen(false)}
        onSave={addDocRow}
      />
      <DocDetailsDialog
        open={docEditOpen}
        onClose={() => setDocEditOpen(false)}
        onSave={handleSaveEdit}
        initial={docEditInitial}
      />
      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={
          isSmallScreen
            ? {}
            : {
                left: "50% !important",
                top: "50% !important",
                bottom: "auto !important",
                transform: "translate(-50%, -50%)",
              }
        }
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

Inputs.propTypes = {
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cycleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  farmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedHatcheryDataId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedInvoiceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
export default forwardRef(Inputs);
const makeTempId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const isTempIdentifier = (value) => {
  if (value === undefined || value === null) return false;
  const str = String(value);
  return str.startsWith("tmp-") || str.startsWith("temp-");
};
const normalizePersistedId = (value) => {
  if (value === undefined || value === null) return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? value : numeric;
};
const getOptimisticId = (rawId) => {
  if (rawId === undefined || rawId === null) return null;
  return isTempIdentifier(rawId) ? rawId : normalizePersistedId(rawId);
};
